import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getLocalizedText } from '../utils/helpers';
import { Badge } from '../components/ui/badge';
import { ServiceCard, CourseCard } from '../components/Cards';
import { GoogleRatingDisplay } from '../components/GoogleRatingDisplay';
import { SEO, generatePersonSchema } from '../components/SEO';
import { ChevronRight, MapPin, Award, Heart, User, GraduationCap, Instagram, Facebook, Youtube, Linkedin, Twitter, Send, Phone, Globe, Mail } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// TikTok icon (not in lucide-react)
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Social Links Component
const SocialLinksDisplay = ({ socialLinks }) => {
  if (!socialLinks) return null;
  
  const links = [
    { key: 'instagram', icon: Instagram, label: 'Instagram' },
    { key: 'facebook', icon: Facebook, label: 'Facebook' },
    { key: 'youtube', icon: Youtube, label: 'YouTube' },
    { key: 'tiktok', icon: TikTokIcon, label: 'TikTok' },
    { key: 'linkedin', icon: Linkedin, label: 'LinkedIn' },
    { key: 'twitter', icon: Twitter, label: 'Twitter' },
    { key: 'telegram', icon: Send, label: 'Telegram' },
  ];
  
  const activeSocials = links.filter(l => socialLinks[l.key]);
  
  if (activeSocials.length === 0) return null;
  
  return (
    <div className="flex flex-wrap gap-3 mt-4">
      {activeSocials.map(({ key, icon: Icon, label }) => (
        <a
          key={key}
          href={socialLinks[key]}
          target="_blank"
          rel="noopener"
          className="flex items-center gap-2 px-3 py-2 rounded-full bg-gray-100 hover:bg-gold/20 text-secondary hover:text-gold transition-colors"
          style={{ '--tw-text-opacity': 1 }}
          title={label}
        >
          <Icon className="h-4 w-4" />
          <span className="text-sm">{label}</span>
        </a>
      ))}
    </div>
  );
};

export default function TeacherDetailPage() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const [teacher, setTeacher] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const teacherRes = await axios.get(`${API_URL}/api/teachers/${id}`);
        const teacherData = teacherRes.data;
        setTeacher(teacherData);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    };
    load();
  }, [id]);

  if (loading) return <div className="text-center py-12">{t('common.loading')}</div>;
  if (!teacher) return <div className="text-center py-12">Profesional no encontrado</div>;

  const hasImage = teacher.images && teacher.images.length > 0;
  const services = teacher.services || [];
  const courses = teacher.courses || [];

  // SEO data
  const teacherName = teacher.user_name || teacher.name;
  const bioText = getLocalizedText(teacher.bio, i18n.language);
  const seoDescription = bioText ? bioText.substring(0, 155) + '...' : `${teacherName} - Terapeuta tántrico profesional en ${teacher.location?.city || 'España'}`;
  const personSchema = generatePersonSchema(teacher);

  return (
    <>
      <SEO
        title={teacherName}
        description={seoDescription}
        image={hasImage ? teacher.images[0] : undefined}
        url={`/teacher/${id}`}
        type="profile"
        schema={personSchema}
      />
      <div className="max-w-4xl mx-auto px-4 py-8 animate-fade-in" data-testid="teacher-detail-page">
      <button onClick={() => navigate(-1)} className="flex items-center text-secondary mb-6 hover:text-gold">
        <ChevronRight className="h-4 w-4 rotate-180 mr-1" /> {t('common.back')}
      </button>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden p-8">
        <div className="flex items-start gap-6 mb-6">
          <div className="w-24 h-24 rounded-full bg-cream-dark flex items-center justify-center flex-shrink-0 overflow-hidden" style={{ backgroundColor: '#F5EFE6' }}>
            {hasImage ? (
              <img src={teacher.images[0]} alt={teacher.user_name || teacher.name} className="w-full h-full object-cover" />
            ) : (
              <User className="h-12 w-12 text-gold" style={{ color: '#D4A574' }} />
            )}
          </div>
          <div>
            <h1 className="font-serif text-3xl font-bold text-primary">{teacher.user_name || teacher.name}</h1>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <Badge className="bg-purple-500 text-white">Profesional</Badge>
              {teacher.has_school_profile && <Badge className="bg-blue-500 text-white">Escuela</Badge>}
            </div>
            <div className="flex items-center text-secondary mt-2">
              <MapPin className="h-4 w-4 mr-1" />
              {teacher.location?.city}{teacher.location?.country && `, ${teacher.location.country}`}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-cream rounded-lg p-4" style={{ backgroundColor: '#F5EFE6' }}>
            <Award className="h-5 w-5 text-gold mb-2" style={{ color: '#D4A574' }} />
            <p className="text-sm text-secondary">Experiencia</p>
            <p className="font-semibold">{teacher.experience_years} años</p>
          </div>
          <div className="bg-cream rounded-lg p-4" style={{ backgroundColor: '#F5EFE6' }}>
            <Heart className="h-5 w-5 text-gold mb-2" style={{ color: '#D4A574' }} />
            <p className="text-sm text-secondary">Servicios</p>
            <p className="font-semibold">{services.length}</p>
          </div>
          {courses.length > 0 && (
            <div className="bg-cream rounded-lg p-4" style={{ backgroundColor: '#F5EFE6' }}>
              <GraduationCap className="h-5 w-5 text-gold mb-2" style={{ color: '#D4A574' }} />
              <p className="text-sm text-secondary">Cursos</p>
              <p className="font-semibold">{courses.length}</p>
            </div>
          )}
        </div>

        {/* Google Business Rating */}
        {teacher.google_rating_verified && teacher.google_rating && (
          <div className="mb-6">
            <GoogleRatingDisplay
              rating={teacher.google_rating}
              reviewsCount={teacher.google_reviews_count}
              mapsUrl={teacher.google_maps_url}
              isVerified={teacher.google_rating_verified}
            />
          </div>
        )}

        <div className="mb-6">
          <h3 className="font-serif text-xl font-semibold mb-3">{t('teacher.bio')}</h3>
          <p className="text-secondary whitespace-pre-line">{getLocalizedText(teacher.bio, i18n.language)}</p>
        </div>

        {teacher.specialties?.length > 0 && (
          <div className="mb-6">
            <h3 className="font-serif text-xl font-semibold mb-3">{t('teacher.specialties')}</h3>
            <div className="flex flex-wrap gap-2">
              {teacher.specialties.map((s, i) => (
                <Badge key={i} variant="outline">{s}</Badge>
              ))}
            </div>
          </div>
        )}

        {teacher.certifications?.length > 0 && (
          <div className="mb-6">
            <h3 className="font-serif text-xl font-semibold mb-3">{t('teacher.certifications')}</h3>
            <ul className="list-disc list-inside text-secondary">
              {teacher.certifications.map((c, i) => (
                <li key={i}>{c}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Social Links */}
        {teacher.social_links && (
          <div className="mb-6">
            <h3 className="font-serif text-xl font-semibold mb-3">Redes Sociales</h3>
            <SocialLinksDisplay socialLinks={teacher.social_links} />
          </div>
        )}

        {/* Contact Information */}
        {(teacher.phone || teacher.website || teacher.email) && (
          <div className="mb-6">
            <h3 className="font-serif text-xl font-semibold mb-3">Contacto</h3>
            <div className="flex flex-wrap gap-3">
              {teacher.phone && (
                <a
                  href={`tel:${teacher.phone}`}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-green-100 hover:bg-green-200 text-green-700 transition-colors"
                >
                  <Phone className="h-4 w-4" />
                  <span className="text-sm font-medium">{teacher.phone}</span>
                </a>
              )}
              {teacher.website && (
                <a
                  href={teacher.website.startsWith('http') ? teacher.website : `https://${teacher.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-700 transition-colors"
                >
                  <Globe className="h-4 w-4" />
                  <span className="text-sm font-medium">Sitio Web</span>
                </a>
              )}
              {teacher.email && (
                <a
                  href={`mailto:${teacher.email}`}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-purple-100 hover:bg-purple-200 text-purple-700 transition-colors"
                >
                  <Mail className="h-4 w-4" />
                  <span className="text-sm font-medium">{teacher.email}</span>
                </a>
              )}
            </div>
          </div>
        )}

        {/* Services Section */}
        {services.length > 0 && (
          <div className="mb-8">
            <h3 className="font-serif text-xl font-semibold mb-4 flex items-center gap-2">
              <Heart className="h-5 w-5 text-purple-500" />
              {t('profile.myServices')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {services.map((service) => (
                <ServiceCard key={service.id} service={service} language={i18n.language} />
              ))}
            </div>
          </div>
        )}

        {/* Courses Section - only shown if user has school profile */}
        {courses.length > 0 && (
          <div>
            <h3 className="font-serif text-xl font-semibold mb-4 flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-blue-500" />
              {t('profile.myCourses')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {courses.map((course) => (
                <CourseCard key={course.id} course={course} language={i18n.language} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
}
